/**
 * ProjectRow — capped row of community project cards for the Alignment tab.
 *
 * Fetches projects for a community, displays up to `maxVisible` ProjectCards,
 * and shows a "View all N projects →" link when overflow exists.
 */

import React, { useCallback, useEffect, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { fetchProjectsByCommunity, ProjectResponse, toSeedShape } from "../../api/projects";
import { mediaSrc } from "../../api/mediaUrl";
import ProjectCard, { ProjectCardData } from "../ProjectCard/ProjectCard";
import styles from "./ProjectRow.module.css";

interface Props {
  communityGuid: string;
  /** Max cards to show before "View all" link. 0 = unlimited. */
  maxVisible?: number;
  /** Callback when a project card is clicked. */
  onOpen?: (projectGuid: string) => void;
  /** Callback when "View all" is clicked. */
  onViewAll?: () => void;
  /** Section header text (e.g. "Priority Projects"). */
  sectionTitle?: string;
  /** Pre-resolved country flag emoji to display on each card. */
  countryFlag?: string;
  /** Pre-resolved country name to display on each card. */
  countryName?: string;
  /**
   * DEV-970. When true, the "View all N projects →" link renders even
   * when total ≤ maxVisible. Lets the Alignment tab advertise the
   * full-list landing regardless of how many priority projects exist.
   */
  alwaysShowViewAll?: boolean;
}

function toCardData(p: ProjectResponse): ProjectCardData {
  const seed = toSeedShape(p);
  // Resolve MinIO keys to full URLs
  const heroRaw = seed.hero_image_url || "";
  const heroResolved = mediaSrc(heroRaw) ?? "";
  return {
    project_guid: seed.project_GUID,
    project_name: seed.project_name,
    hero_image_url: heroResolved || null,
    tile_image_url: heroResolved || null,
    project_category: seed.project_category,
    project_status: seed.project_status,
    project_reach: seed.project_reach,
    description: seed.description,
    Community: seed.Community,
    community_l3: seed.community_l3,
    community_l2: seed.community_l2,
    community_l1: seed.community_l1,
    primary_location_entity_guid: seed.primary_location_entity_guid,
  };
}

const ProjectRow: React.FC<Props> = ({
  communityGuid,
  maxVisible = 0,
  onOpen,
  onViewAll,
  sectionTitle,
  countryFlag,
  countryName,
  alwaysShowViewAll = false,
}) => {
  const { t } = useAppTranslation("pages");
  const [projects, setProjects] = useState<ProjectResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchProjectsByCommunity(communityGuid);
      setProjects(data);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load projects.");
    } finally {
      setLoading(false);
    }
  }, [communityGuid]);
  // ProjectRow's load() correctly excludes `t` from its deps — kept as a
  // template for the WorkspaceList fix (DEV-968) since i18next `t` is
  // reference-unstable and would trigger an infinite-fetch loop.

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <div className={styles.loading}>Loading…</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  if (projects.length === 0) {
    return null; // Don't render section if no projects
  }

  const hasOverflow = maxVisible > 0 && projects.length > maxVisible;
  const visible = hasOverflow ? projects.slice(0, maxVisible) : projects;
  // DEV-970: "View all" link shows on overflow OR when alwaysShowViewAll
  // is true (regardless of count). Used by the Alignment tab so visitors
  // can always discover the full-list landing.
  const showViewAll = (hasOverflow || alwaysShowViewAll) && !!onViewAll;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.title}>
          {sectionTitle || t("communityDetail.priorityProjects")}
        </span>
      </div>

      <div className={styles.grid}>
        {visible.map((p) => (
          <ProjectCard
            key={p.id}
            project={toCardData(p)}
            countryFlag={countryFlag}
            countryName={countryName}
            onClick={onOpen}
          />
        ))}
      </div>

      {showViewAll && (
        <button type="button" className={styles.viewAllBtn} onClick={onViewAll}>
          {t("communityDetail.viewAllProjects", { count: projects.length })}
          {" \u2192"}
        </button>
      )}
    </div>
  );
};

export default ProjectRow;
